import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Receipt, ChevronUp, ChevronDown } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const formatDelayMonth = (ym) => {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  const dt = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
  return dt.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};

const DelayRow = ({ apt, groupKey, i }) => {
  const aptDate = apt.date ? format(parseISO(apt.date), 'dd/MM/yyyy') : '—';
  return (
    <div
      className="text-xs flex items-center justify-between gap-2"
      data-testid={`payment-delay-row-${groupKey}-${i}`}
    >
      <span className="truncate text-slate-700">
        {apt.client_name} • {apt.service_type}
        <span className="text-slate-400"> — prestation du {aptDate}</span>
      </span>
      <span className="font-mono text-slate-900">
        {Number(apt.price || 0).toFixed(2)} €
      </span>
    </div>
  );
};

const DelayGroup = ({ groupKey, group }) => {
  const items = group.items || [];
  return (
    <div className="space-y-2" data-testid={`payment-delay-group-${groupKey}`}>
      <div className="flex items-center justify-between border-b border-slate-100 pb-1">
        <p className="text-sm font-semibold text-slate-800 capitalize">
          {formatDelayMonth(groupKey)}
        </p>
        <p
          className="text-sm font-mono font-semibold text-amber-700"
          data-testid={`payment-delay-total-${groupKey}`}
        >
          {Number(group.total).toFixed(2)} €
        </p>
      </div>
      <div className="space-y-1">
        {items.map((apt, i) => (
          <DelayRow key={`${apt.id}-${i}`} apt={apt} groupKey={groupKey} i={i} />
        ))}
      </div>
    </div>
  );
};

export const PaymentDelayRecap = ({ appointments }) => {
  const [collapsed, setCollapsed] = useState(false);

  const delayedAppointments = (appointments || []).filter((apt) => apt.payment_delay_month);
  if (delayedAppointments.length === 0) return null;

  const grouped = {};
  let grandTotal = 0;
  for (let i = 0; i < delayedAppointments.length; i += 1) {
    const apt = delayedAppointments[i];
    const key = apt.payment_delay_month;
    if (!grouped[key]) grouped[key] = { items: [], total: 0 };
    grouped[key].items.push(apt);
    grouped[key].total += Number(apt.price || 0);
    grandTotal += Number(apt.price || 0);
  }
  const sortedKeys = Object.keys(grouped).sort();

  return (
    <Card
      className="bg-white border border-amber-200 shadow-sm mt-6"
      data-testid="payment-delay-recap-card"
    >
      <CardHeader
        className="border-b border-amber-100 bg-amber-50/40 cursor-pointer select-none"
        onClick={() => setCollapsed(!collapsed)}
        data-testid="payment-delay-recap-toggle"
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Receipt className="w-5 h-5 text-amber-600 shrink-0" />
            <CardTitle
              className="font-outfit text-lg text-slate-900"
              data-testid="payment-delay-recap-title"
            >
              Récap délais de paiement
            </CardTitle>
            {collapsed && (
              <span
                className="text-xs font-mono font-semibold text-amber-700 ml-2"
                data-testid="payment-delay-recap-grandtotal"
              >
                {Number(grandTotal).toFixed(2)} €
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setCollapsed(!collapsed);
            }}
            className="w-7 h-7 rounded-md border border-amber-200 bg-white hover:bg-amber-50 text-amber-700 flex items-center justify-center shrink-0 transition-colors"
            aria-label={collapsed ? 'Déplier' : 'Réduire'}
            data-testid="payment-delay-recap-collapse-btn"
          >
            {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
        </div>
        {!collapsed && (
          <p className="text-xs text-slate-500 mt-1">
            Prestations dont le règlement est attendu sur un mois différent du mois de la prestation.
          </p>
        )}
      </CardHeader>
      {!collapsed && (
        <CardContent className="p-4 space-y-4" data-testid="payment-delay-recap-content">
          {sortedKeys.map((key) => (
            <DelayGroup key={key} groupKey={key} group={grouped[key]} />
          ))}
        </CardContent>
      )}
    </Card>
  );
};

export default PaymentDelayRecap;
